import React, { useState, useEffect, useCallback } from 'react';
import { AlertCircle, Loader2, Headphones, Download, Upload, File, X, CheckCircle, Plus } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { fetchAircallRecording } from '../lib/aircall';
import { supabase } from '../lib/auth';

interface CallSectionProps {
  callId?: string | null;
  supabaseRecordingPath?: string | null;
  appointmentId: string;
  onRecordingUploaded?: (recordingPath: string) => void;
}

const CallSection: React.FC<CallSectionProps> = ({
  callId,
  supabaseRecordingPath,
  appointmentId,
  onRecordingUploaded
}) => {
  // États pour l'enregistrement final
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasUploadedFile, setHasUploadedFile] = useState(false);
  const [currentRecordingPath, setCurrentRecordingPath] = useState<string | null>(null);

  // États pour upload
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);

  const generateSupabaseSignedUrl = useCallback(async (path: string) => {
    setIsLoading(true);
    setError(null);

    try {
      const { data, error } = await supabase.storage
        .from('calls')
        .createSignedUrl(path, 3600);

      if (error) throw error;

      if (data?.signedUrl) {
        setSignedUrl(data.signedUrl);
      } else {
        throw new Error('Impossible de générer l\'URL signée');
      }
    } catch (err: any) {
      console.error('Erreur lors de la génération de l\'URL signée Supabase:', err);
      setError(err.message || 'Erreur lors du chargement de l\'enregistrement');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const generateAircallUrl = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const url = await fetchAircallRecording(callId!);
      if (url) {
        setSignedUrl(url);
      } else {
        setError('Enregistrement Aircall non disponible');
      }
    } catch (err: any) {
      console.error('Erreur lors de la récupération Aircall:', err);
      setError(err.message || 'Erreur lors du chargement de l\'enregistrement Aircall');
    } finally {
      setIsLoading(false);
    }
  }, [callId]);

  // Charger l'enregistrement au montage du composant
  useEffect(() => {
    const loadRecording = async () => {
      // Vérifier d'abord si on a un enregistrement uploadé (priorité)
      if (supabaseRecordingPath) {
        setHasUploadedFile(true);
        setCurrentRecordingPath(supabaseRecordingPath);
        await generateSupabaseSignedUrl(supabaseRecordingPath);
        return;
      }

      // Sinon, essayer Aircall
      if (callId) {
        await generateAircallUrl();
        return;
      }

      setIsLoading(false);
    };

    loadRecording();
  }, [callId, supabaseRecordingPath, generateSupabaseSignedUrl, generateAircallUrl]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    setUploadError(null);
    setUploadSuccess(false);

    if (!file) {
      setSelectedFile(null);
      return;
    }

    // Validation du fichier
    const maxSize = 50 * 1024 * 1024; // 50MB
    if (file.size > maxSize) {
      setUploadError('Le fichier est trop volumineux (max 50MB)');
      return;
    }

    const allowedTypes = ['audio/mpeg', 'audio/wav', 'audio/mp3'];
    if (!allowedTypes.includes(file.type) && !file.name.toLowerCase().match(/\.(mp3|wav)$/)) {
      setUploadError('Format non supporté. Utilisez MP3 ou WAV.');
      return;
    }

    setSelectedFile(file);
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setIsUploading(true);
    setUploadError(null);

    try {
      const fileExtension = selectedFile.name.split('.').pop() || 'mp3';
      const fileName = `${appointmentId}.${fileExtension}`;

      // Si on a déjà un recording_path avec une extension différente, supprimer l'ancien fichier
      if (currentRecordingPath && currentRecordingPath !== fileName) {
        console.log('Suppression de l\'ancien fichier:', currentRecordingPath);
        const { error: deleteError } = await supabase.storage
          .from('calls')
          .remove([currentRecordingPath]);

        if (deleteError) {
          console.warn('Erreur lors de la suppression de l\'ancien fichier:', deleteError);
        } else {
          console.log('Ancien fichier supprimé avec succès');
        }
      }

      // Upload vers Supabase Storage avec upsert pour écraser si existe
      console.log('Upload du fichier:', fileName);
      const { error: uploadError, data: uploadData } = await supabase.storage
        .from('calls')
        .upload(fileName, selectedFile, {
          upsert: true,
          contentType: selectedFile.type || 'audio/mpeg'
        });

      if (uploadError) {
        console.error('Erreur d\'upload:', uploadError);
        throw uploadError;
      }

      console.log('Upload réussi:', uploadData);

      // Mettre à jour la base de données
      const { error: updateError } = await supabase
        .from('appointments')
        .update({ recording_path: fileName })
        .eq('id', appointmentId);

      if (updateError) throw updateError;

      setUploadSuccess(true);
      setHasUploadedFile(true);
      setCurrentRecordingPath(fileName);
      onRecordingUploaded?.(fileName);

      // Recharger avec le nouveau fichier
      await generateSupabaseSignedUrl(fileName);

      // Reset form et fermer modal
      setSelectedFile(null);
      setShowUploadModal(false);

      setTimeout(() => {
        setUploadSuccess(false);
      }, 3000);

    } catch (err: any) {
      console.error('Erreur lors de l\'upload:', err);
      setUploadError(err.message || 'Erreur lors de l\'upload du fichier');
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setUploadError(null);
  };

  const renderUploadSection = () => (
    <AnimatePresence>
      {showUploadModal && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          className="overflow-hidden"
        >
          <div className="mt-4 p-6 bg-gray-50 rounded-lg border-2 border-blue-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-gray-900">
                {hasUploadedFile ? 'Remplacer l\'enregistrement' : 'Ajouter un enregistrement'}
              </h3>
              <button
                onClick={() => {
                  setShowUploadModal(false);
                  setSelectedFile(null);
                  setUploadError(null);
                }}
                className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded-lg transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Messages d'erreur */}
            <AnimatePresence>
              {uploadError && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center space-x-2"
                >
                  <AlertCircle className="h-4 w-4 text-red-600 flex-shrink-0" />
                  <span className="text-sm text-red-800">{uploadError}</span>
                </motion.div>
              )}
            </AnimatePresence>

            {!selectedFile ? (
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-blue-400 hover:bg-blue-50 transition-all duration-200 relative bg-white">
                <div className="flex flex-col items-center space-y-3">
                  <Upload className="h-10 w-10 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-700 font-medium mb-1">Glissez un fichier audio ici</p>
                    <p className="text-xs text-gray-500 mb-1">ou cliquez pour sélectionner</p>
                    <p className="text-xs text-gray-400">MP3, WAV • Max 50MB</p>
                  </div>
                  <input
                    type="file"
                    accept=".mp3,.wav,audio/mpeg,audio/wav"
                    onChange={handleFileSelect}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    disabled={isUploading}
                  />
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="bg-white rounded-lg p-4 border border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3 flex-1 min-w-0">
                      <File className="h-5 w-5 text-blue-600 flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-gray-900 truncate">{selectedFile.name}</p>
                        <p className="text-xs text-gray-500">{(selectedFile.size / 1024 / 1024).toFixed(1)} MB</p>
                      </div>
                    </div>
                    <button
                      onClick={handleRemoveFile}
                      className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors flex-shrink-0 ml-2"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                <div className="flex space-x-2">
                  <button
                    onClick={() => {
                      setShowUploadModal(false);
                      setSelectedFile(null);
                    }}
                    disabled={isUploading}
                    className="flex-1 px-4 py-2 text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={handleUpload}
                    disabled={isUploading}
                    className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50 flex items-center justify-center space-x-2"
                  >
                    {isUploading ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span>Upload...</span>
                      </>
                    ) : (
                      <>
                        <Upload className="h-4 w-4" />
                        <span>{hasUploadedFile ? 'Remplacer' : 'Uploader'}</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  // État de chargement
  if (isLoading) {
    return (
      <div className="flex items-center space-x-3 py-3">
        <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
        <span className="text-sm text-gray-600">Chargement de l'enregistrement...</span>
      </div>
    );
  }

  // Si on a un enregistrement (Supabase ou Aircall)
  if (signedUrl) {
    return (
      <div className="space-y-4">
        {/* Lecteur audio avec petit bouton d'upload */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Headphones className="h-4 w-4 text-blue-600" />
              <span className="text-sm font-medium text-gray-700">
                {hasUploadedFile ? 'Enregistrement uploadé' : 'Enregistrement Aircall'}
              </span>
            </div>
            <div className="flex items-center space-x-2">
              {/* Petit bouton d'upload discret */}
              <button
                onClick={() => setShowUploadModal(true)}
                className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all duration-200 group"
                title="Remplacer l'enregistrement"
              >
                <Upload className="h-4 w-4 group-hover:scale-110 transition-transform" />
              </button>
              <a
                href={signedUrl}
                download
                className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-all duration-200 group"
                title="Télécharger"
              >
                <Download className="h-4 w-4 group-hover:scale-110 transition-transform" />
              </a>
            </div>
          </div>

          <audio
            controls
            className="w-full h-8 rounded-lg"
            preload="metadata"
            style={{
              filter: 'sepia(20%) saturate(70%) hue-rotate(315deg) brightness(95%) contrast(105%)'
            }}
          >
            <source src={signedUrl} type="audio/mpeg" />
            <source src={signedUrl} type="audio/wav" />
            Votre navigateur ne supporte pas la lecture audio.
          </audio>
        </div>

        {/* Messages de succès */}
        <AnimatePresence>
          {uploadSuccess && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="p-2 bg-green-50 border border-green-200 rounded-lg flex items-center space-x-2"
            >
              <CheckCircle className="h-4 w-4 text-green-600" />
              <span className="text-xs text-green-800">Nouvel enregistrement uploadé !</span>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Section d'upload inline */}
        {renderUploadSection()}
      </div>
    );
  }

  // Si erreur ou pas d'enregistrement disponible
  if (error || (!callId && !supabaseRecordingPath)) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <AlertCircle className="h-4 w-4 text-orange-600" />
            <span className="text-sm text-gray-600">
              {error || 'Aucun enregistrement disponible'}
            </span>
          </div>

          {/* Petit bouton d'upload */}
          <button
            onClick={() => setShowUploadModal(true)}
            className="flex items-center space-x-2 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-medium transition-all duration-200 shadow-sm hover:shadow-md"
          >
            <Plus className="h-3 w-3" />
            <span>Ajouter</span>
          </button>
        </div>

        {/* Messages de succès */}
        <AnimatePresence>
          {uploadSuccess && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="p-2 bg-green-50 border border-green-200 rounded-lg flex items-center space-x-2"
            >
              <CheckCircle className="h-4 w-4 text-green-600" />
              <span className="text-xs text-green-800">Enregistrement uploadé !</span>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Section d'upload inline */}
        {renderUploadSection()}
      </div>
    );
  }

  // Aucun enregistrement disponible - petit bouton d'ajout
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-sm text-gray-600">Aucun enregistrement disponible</span>
        <button
          onClick={() => setShowUploadModal(true)}
          className="flex items-center space-x-2 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-medium transition-all duration-200 shadow-sm hover:shadow-md"
        >
          <Plus className="h-3 w-3" />
          <span>Ajouter</span>
        </button>
      </div>

      {/* Messages de succès */}
      <AnimatePresence>
        {uploadSuccess && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="p-2 bg-green-50 border border-green-200 rounded-lg flex items-center space-x-2"
          >
            <CheckCircle className="h-4 w-4 text-green-600" />
            <span className="text-xs text-green-800">Enregistrement uploadé !</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Section d'upload inline */}
      {renderUploadSection()}
    </div>
  );
};

export default CallSection;
